<template>
  <section>
    <h1>{{ $t("membership") }}</h1>
  </section>

  <section class="mb-5">
    <h2>{{ $t("currentMembership") }}</h2>

    <p class="py-2">
      <span
        :class="`bg-${remainingDaysColor}`"
        class="py-2 px-3 rounded text-white"
      >
        {{ $tc("remaining", remainingDays) }}
      </span>
    </p>

    <p>
      <router-link
        :to="{ name: 'membership.extend' }"
        class="btn btn-secondary"
      >
        {{ $t("extendMembership") }}
      </router-link>
    </p>
  </section>

  <section v-if="affiliates.length > 0" class="mb-5">
    <h2>{{ $t("membershipPage.affiliateHistory") }}</h2>

    <div class="row">
      <div class="col-12 col-lg-6">
        <ul v-if="affiliateLimit" class="list-group">
          <li
            v-for="affiliate in affiliates.slice(0, 10)"
            :key="affiliate.id"
            class="list-group-item"
          >
            {{ affiliate.affiliate }}
            <span class="badge bg-secondary text-dark ms-3">{{
              new Date(affiliate.date).toLocaleDateString($i18n.locale, {
                year: "numeric",
                month: "2-digit",
                day: "2-digit",
              })
            }}</span>
          </li>
        </ul>

        <ul v-else class="list-group">
          <li
            v-for="affiliate in affiliates"
            :key="affiliate.id"
            class="list-group-item"
          >
            {{ affiliate.affiliate }}
            <span class="badge bg-secondary text-dark ms-3">{{
              new Date(affiliate.date).toLocaleDateString($i18n.locale, {
                year: "numeric",
                month: "2-digit",
                day: "2-digit",
              })
            }}</span>
          </li>
        </ul>

        <button
          v-if="affiliates.length > 10"
          class="btn btn-light mt-4"
          @click.prevent="toggleAffiliateLimit"
        >
          <span v-if="affiliateLimit">{{ $t("membershipPage.showAll") }}</span>
          <span v-else>{{ $t("membershipPage.hideAll") }}</span>
        </button>
      </div>
    </div>
  </section>

  <section v-if="feedbacks.length > 0" class="mb-5">
    <h2>{{ $t("membershipPage.feedbackHistory") }}</h2>

    <div class="row">
      <div class="col-12 col-lg-6">
        <ul v-if="feedbackLimit" class="list-group">
          <li
            v-for="feedback in feedbacks.slice(0, 10)"
            :key="feedback.id"
            class="list-group-item"
          >
            {{
              new Date(feedback.date).toLocaleDateString($i18n.locale, {
                year: "numeric",
                month: "2-digit",
                day: "2-digit",
              })
            }}
            <span class="badge bg-secondary text-dark ms-3"
              >+ {{ feedback.period }}
              {{ $t("membershipPage.feedbackPlusDays") }}</span
            >
          </li>
        </ul>

        <ul v-else class="list-group">
          <li
            v-for="feedback in feedbacks"
            :key="feedback.id"
            class="list-group-item"
          >
            {{
              new Date(feedback.date).toLocaleDateString($i18n.locale, {
                year: "numeric",
                month: "2-digit",
                day: "2-digit",
              })
            }}
            <span class="badge bg-secondary text-dark ms-3"
              >+ {{ feedback.period }}
              {{ $t("membershipPage.feedbackPlusDays") }}</span
            >
          </li>
        </ul>

        <button
          v-if="feedbacks.length > 10"
          class="btn btn-light mt-4"
          @click.prevent="toggleFeedbackLimit"
        >
          <span v-if="feedbackLimit">{{ $t("membershipPage.showAll") }}</span>
          <span v-else>{{ $t("membershipPage.hideAll") }}</span>
        </button>
      </div>
    </div>
  </section>

  <section>
    <h2>{{ $t("membershipPage.allMemberships") }}</h2>

    <div class="row">
      <div class="col-12 col-lg-6">
        <div id="membershipAccordion" class="accordion">
          <div
            v-for="purchase in purchases"
            :key="purchase.id"
            class="accordion-item"
          >
            <h2 :id="`heading${purchase.id}`" class="accordion-header">
              <button
                :aria-controls="`collapse${purchase.id}`"
                :data-bs-target="`#collapse${purchase.id}`"
                aria-expanded="false"
                class="accordion-button collapsed"
                data-bs-toggle="collapse"
                type="button"
              >
                {{ $t(`membershipNames.${purchase.name}`) }}
                <span v-if="purchase.current" class="ms-3 badge bg-success">{{
                  $t("membershipPage.current")
                }}</span>
                <span v-if="purchase.expired" class="ms-3 badge bg-danger">{{
                  $t("membershipPage.expired")
                }}</span>
              </button>
            </h2>
            <div
              :id="`collapse${purchase.id}`"
              :aria-labelledby="`heading${purchase.id}`"
              class="accordion-collapse collapse"
              data-bs-parent="#membershipAccordion"
            >
              <div class="accordion-body">
                <div class="table-responsive">
                  <table class="table-borderless">
                    <tbody>
                      <tr>
                        <th class="pe-3" scope="row">
                          {{ $t("membershipPage.purchaseDate") }}
                        </th>
                        <td>
                          {{
                            new Date(purchase.date).toLocaleDateString(
                              $i18n.locale,
                              {
                                year: "numeric",
                                month: "2-digit",
                                day: "2-digit",
                                hour: "2-digit",
                                minute: "2-digit",
                              }
                            )
                          }}
                        </td>
                      </tr>

                      <tr>
                        <th class="pe-3" scope="row">
                          {{ $t("membershipPage.price") }}
                        </th>
                        <td>{{ purchase.price }} €</td>
                      </tr>

                      <tr>
                        <th class="pe-3" scope="row">
                          {{ $t("membershipPage.status") }}
                        </th>
                        <td>
                          <span v-if="!purchase.current && !purchase.expired">{{
                            $t("membershipPage.notUsed")
                          }}</span>
                          <span v-if="purchase.current">{{
                            $t("membershipPage.active")
                          }}</span>
                          <span v-if="purchase.expired">{{
                            $t("membershipPage.expired")
                          }}</span>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                <div v-if="purchase.revocable" class="mt-4">
                  <RevokePurchase :purchase="purchase" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import RevokePurchase from "../components/membership/RevokePurchase";
import { mapGetters } from "vuex";
export default {
  name: "Membership",
  components: { RevokePurchase },
  data() {
    return {
      affiliateLimit: true,
      feedbackLimit: true,
    };
  },
  computed: {
    ...mapGetters({
      remainingDays: "membership/getRemainingDays",
      purchases: "membership/getPurchases",
      affiliates: "membership/getAffiliates",
      feedbacks: "membership/getFeedbackHistory",
    }),
    remainingDaysColor() {
      return this.remainingDays > 10 ? "success" : "danger";
    },
  },
  methods: {
    toggleAffiliateLimit() {
      this.affiliateLimit = !this.affiliateLimit;
    },
    toggleFeedbackLimit() {
      this.feedbackLimit = !this.feedbackLimit;
    },
  },
};
</script>

<style scoped></style>
